import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { LogIn, Info, Clock, ShoppingCart, Sparkles, Award, LayoutDashboard, Tag, Package, ChevronDown, ChevronUp, Instagram, Facebook, Phone, CheckCircle2 } from "lucide-react";
import { MarkdownView } from "../components/MarkdownEditor";
import { computeCart } from "../lib/cart";
import CheckoutModal from "../components/CheckoutModal";
import CountdownBadge from "../components/CountdownBadge";
import OfferCodeCard from "../components/OfferCodeCard";

const fmtPrice = (n) =>
  n === 0
    ? "Gratis"
    : new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
      }).format(n);

const fmtHours = (h) => {
  if (!h) return null;
  return Number.isInteger(h) ? `${h} h` : `${h.toFixed(1)} h`;
};

const headerImageUrl = (c) =>
  c.has_header_image
    ? `${API}/public/courses/${c.id}/header${c.header_version ? `?v=${c.header_version}` : ""}`
    : null;

/** What a promotion covers, for the ribbon copy. Unset means everything. */
const scopeNoun = (promo, plural = true) => {
  const scope = promo?.applies_to || "both";
  if (scope === "courses") return plural ? "cursos" : "curso";
  if (scope === "workshops") return plural ? "talleres" : "taller";
  return plural ? "cursos o talleres" : "curso o taller";
};

export default function HomePage() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [promotions, setPromotions] = useState([]);
  // Discount codes the admin chose to advertise as a card in the catalogue.
  const [featuredCodes, setFeaturedCodes] = useState([]);
  const [infoCourse, setInfoCourse] = useState(null);
  const [activeArea, setActiveArea] = useState("all"); // "all" or specific area string ("" for "Otros cursos")
  const [homeMoreInfo, setHomeMoreInfo] = useState("");
  const [homeHighlight, setHomeHighlight] = useState("");
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [expiredPromoIds, setExpiredPromoIds] = useState(() => new Set());

  const markPromoExpired = useCallback((id) => {
    if (!id) return;
    setExpiredPromoIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const activePromotions = useMemo(
    () => promotions.filter((p) => !expiredPromoIds.has(p.id)),
    [promotions, expiredPromoIds],
  );

  useEffect(() => {
    Promise.all([
      api.get("/public/catalog"),
      api.get("/public/promotions"),
      api.get("/public/home-more-info").catch(() => ({ data: { text: "", highlight: "" } })),
      // Advertised discount codes. Non-blocking: the catalogue must render
      // even if this fails.
      api.get("/public/discount-codes/featured").catch(() => ({ data: [] })),
    ])
      .then(([c, p, mi, fc]) => {
        setCourses(c.data);
        setPromotions(p.data);
        setHomeMoreInfo(mi?.data?.text || "");
        setHomeHighlight(mi?.data?.highlight || "");
        setFeaturedCodes(fc?.data || []);
      })
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const cart = useMemo(() => {
    const items = courses.filter((c) => selected.has(c.id));
    return computeCart(items, activePromotions);
  }, [courses, selected, activePromotions]);

  const ctaTarget = user ? (["gestion", "admin", "superadmin"].includes(user.role) ? "/admin" : "/dashboard") : "/login";
  const ctaLabel = user ? "Mi panel" : "Acceso a plataforma";

  const bulkPromo = useMemo(
    () => activePromotions.find((p) => p.kind === "bulk" && (p.bulk_percent || 0) > 0),
    [activePromotions],
  );

  // Closest quantity offer the buyer is short of completing, so the cart can
  // nudge them. Only counts courses still loose (a package already took its
  // own) and inside the offer's scope.
  const quantityNudge = useMemo(() => {
    const offers = activePromotions.filter(
      (p) => p.kind === "quantity" && (p.quantity_count || 0) >= 2 && p.fixed_price != null,
    );
    let best = null;
    for (const promo of offers) {
      const eligible = cart.lines.filter((l) => {
        const scope = promo.applies_to || "both";
        if (scope === "both") return true;
        const kind = l.course?.kind;
        return kind == null || kind === (scope === "courses" ? "final" : "normal");
      }).length;
      // Nothing to nudge about with an empty basket or an already-full group.
      if (eligible === 0) continue;
      const missing = promo.quantity_count - (eligible % promo.quantity_count);
      if (missing === promo.quantity_count) continue;
      if (!best || missing < best.missing) best = { promo, missing };
    }
    return best;
  }, [activePromotions, cart.lines]);

  // Offers that aren't tied to particular courses get a ribbon at the top:
  // the bulk discount and every "N cursos por X €". A package isn't here —
  // it names its courses, so it gets its own card further down.
  const ribbonPromos = useMemo(
    () => [
      ...(bulkPromo ? [bulkPromo] : []),
      ...activePromotions.filter(
        (p) => p.kind === "quantity" && (p.quantity_count || 0) >= 2 && p.fixed_price != null,
      ),
    ],
    [activePromotions, bulkPromo],
  );

  // Group courses by area: empty area becomes "Otros cursos" (rendered last).
  // Within each area, the server's sort order (created_at desc) is preserved.
  const groupedCourses = useMemo(() => {
    const groups = new Map();
    courses.forEach((c) => {
      const area = (c.area && c.area.trim()) || "";
      if (!groups.has(area)) groups.set(area, []);
      groups.get(area).push(c);
    });
    const entries = [...groups.entries()];
    entries.sort(([a], [b]) => {
      if (a === "" && b !== "") return 1;
      if (b === "" && a !== "") return -1;
      return a.localeCompare(b, "es", { sensitivity: "base" });
    });
    return entries.map(([area, list]) => ({
      areaKey: area,
      areaLabel: area || "Otros cursos",
      list,
    }));
  }, [courses]);

  // Filter chips: only render the groups matching the active filter.
  const visibleGroups = useMemo(() => {
    if (activeArea === "all") return groupedCourses;
    return groupedCourses.filter((g) => g.areaKey === activeArea);
  }, [groupedCourses, activeArea]);

  // Package promotions rendered as virtual catalog cards with thumbnails.
  const packagePromos = useMemo(() => {
    const byId = Object.fromEntries(courses.map((c) => [c.id, c]));
    return (activePromotions || [])
      .filter(
        (p) =>
          p.kind === "package" &&
          Array.isArray(p.course_ids) &&
          p.course_ids.length >= 2 &&
          p.course_ids.every((cid) => byId[cid]),
      )
      .map((p) => {
        const items = p.course_ids.map((cid) => byId[cid]);
        const originalSum = items.reduce((acc, c) => acc + (Number(c.price) || 0), 0);
        const fullySelected = items.every((c) => selected.has(c.id));
        return {
          promo: p,
          items,
          originalSum,
          savings: Math.max(0, originalSum - (p.fixed_price || 0)),
          fullySelected,
        };
      });
  }, [activePromotions, courses, selected]);

  const togglePackage = (pkg) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = pkg.items.every((c) => next.has(c.id));
      if (allSelected) {
        pkg.items.forEach((c) => next.delete(c.id));
      } else {
        pkg.items.forEach((c) => next.add(c.id));
      }
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-[#F4F6F4]" data-testid="home-page">
      {/* Top bar */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <img src="/logo.jpg" alt="laclaseencasa" className="w-16 h-16 sm:w-20 sm:h-20 object-contain rounded-md" />
            <div className="leading-tight">
              <p className="font-display text-lg text-brand-ink">laclaseencasa</p>
              <p className="text-[10px] tracking-[0.2em] uppercase text-gray-500 font-mono-data">
                de SIMA Asistencial
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="https://www.instagram.com/laclaseencasa/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Síguenos en Instagram"
              className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-600 hover:text-brand hover:border-brand transition-colors"
              data-testid="home-instagram-link"
            >
              <Instagram className="w-4 h-4" />
            </a>
            <a
              href="https://www.facebook.com/laclaseencasa/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Síguenos en Facebook"
              className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-600 hover:text-brand hover:border-brand transition-colors"
              data-testid="home-facebook-link"
            >
              <Facebook className="w-4 h-4" />
            </a>
            <Link to={ctaTarget}>
              <Button
                className="bg-brand hover:bg-brand-hover text-white font-medium tracking-wide"
                data-testid="cta-access-platform"
              >
                {user ? <LayoutDashboard className="w-4 h-4 mr-2" /> : <LogIn className="w-4 h-4 mr-2" />}
                {ctaLabel}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-brand/5 border-b border-brand/10">
        <div className="max-w-7xl mx-auto px-6 py-14 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-[11px] tracking-[0.25em] uppercase text-brand font-mono-data mb-3">
              · Formación profesional sociosanitaria no reglada ·
            </p>
            <h1 className="font-display text-[1.9125rem] sm:text-[2.55rem] lg:text-[3.1875rem] text-brand-ink leading-tight">
              Fórmate con SIMA.
              <br />
              Aprende, trabaja, avanza.
            </h1>
            <p className="text-gray-600 mt-5 max-w-md">
              Cursos y Talleres online auto-evaluables con diploma y certificado propios.
              Selecciona los que te interesen y obtén descuento por lotes.
            </p>
            {homeHighlight && homeHighlight.trim() && (
              <p
                className="text-brand-ink font-semibold mt-4 max-w-md whitespace-pre-line"
                data-testid="home-sociosanitaria-highlight"
              >
                {homeHighlight}
              </p>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3 max-w-[30.8rem] md:justify-self-end">
            <Card className="p-[1.1rem] border border-gray-200 shadow-none rounded-lg bg-white">
              <Award className="w-[1.375rem] h-[1.375rem] text-brand mb-2" />
              <p className="text-[11px] uppercase tracking-wider text-gray-500">Certificados</p>
              <p className="text-[0.9625rem] text-brand-ink font-medium">y diplomas</p>
            </Card>
            <Card className="p-[1.1rem] border border-gray-200 shadow-none rounded-lg bg-white">
              <Clock className="w-[1.375rem] h-[1.375rem] text-brand mb-2" />
              <p className="text-[11px] uppercase tracking-wider text-gray-500">Horas</p>
              <p className="text-[0.9625rem] text-brand-ink font-medium">lectivas reconocidas</p>
            </Card>
            <Card className="p-[1.1rem] border border-gray-200 shadow-none rounded-lg bg-white">
              <Sparkles className="w-[1.375rem] h-[1.375rem] text-brand mb-2" />
              <p className="text-[11px] uppercase tracking-wider text-gray-500">Repite</p>
              <p className="text-[0.9625rem] text-brand-ink font-medium">hasta aprobar</p>
            </Card>
            <a
              href="tel:+34910105748"
              className="col-span-3 rounded-lg border border-brand/30 bg-white px-[1.1rem] py-[0.825rem] flex items-center justify-center gap-2 hover:border-brand hover:bg-brand/5 transition-colors group"
              data-testid="home-phone-link"
            >
              <Phone className="w-[1.1rem] h-[1.1rem] text-brand group-hover:scale-110 transition-transform" />
              <span className="text-[0.825rem] uppercase tracking-wider text-gray-500">Más info:</span>
              <span className="text-[0.9625rem] text-brand-ink font-semibold font-mono-data">91 010 57 48</span>
            </a>
          </div>
        </div>
        {/* "+ info" expandable block — content edited from admin settings */}
        {homeMoreInfo && homeMoreInfo.trim() && (
          <div className="max-w-7xl mx-auto px-6 pb-10 -mt-2" data-testid="home-more-info-section">
            <button
              type="button"
              onClick={() => setShowMoreInfo((v) => !v)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:text-brand-hover transition-colors"
              data-testid="home-more-info-toggle"
              aria-expanded={showMoreInfo}
            >
              {showMoreInfo ? (
                <>
                  <ChevronUp className="w-4 h-4" /> Ocultar información
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" /> + info
                </>
              )}
            </button>
            {showMoreInfo && (
              <div
                className="mt-4 p-5 bg-white border border-gray-200 rounded-lg max-w-3xl"
                data-testid="home-more-info-content"
              >
                <MarkdownView text={homeMoreInfo} />
              </div>
            )}
          </div>
        )}
      </section>

      {/* Catalog */}
      <main
        className={`max-w-7xl mx-auto px-6 py-12 ${selected.size > 0 ? "pb-44 md:pb-40" : "pb-12"}`}
      >
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <h2 className="font-display text-3xl text-brand-ink">Catálogo de cursos</h2>
            <p className="text-sm text-gray-500 mt-1">Marca uno o varios cursos.</p>
          </div>
          <div className="flex items-center gap-3">
            {cart.lines.length + cart.appliedPackages.length > 0 && (
              <span className="text-sm text-gray-600 font-mono-data" data-testid="selected-summary-inline">
                {selected.size} seleccionado{selected.size === 1 ? "" : "s"}
              </span>
            )}
          </div>
        </div>

        {/* Filter chips by area */}
        {!loading && groupedCourses.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="area-filter-chips">
            <button
              type="button"
              onClick={() => setActiveArea("all")}
              className={
                "text-xs px-3 py-1.5 rounded-full border transition-colors " +
                (activeArea === "all"
                  ? "bg-brand text-white border-brand"
                  : "bg-white text-gray-700 border-gray-200 hover:border-brand/40")
              }
              data-testid="area-chip-all"
            >
              Todas
              <span className="ml-1.5 text-[10px] opacity-70 font-mono-data">
                {courses.length}
              </span>
            </button>
            {groupedCourses.map((g) => (
              <button
                key={g.areaKey || "__empty__"}
                type="button"
                onClick={() => setActiveArea(g.areaKey)}
                className={
                  "text-xs px-3 py-1.5 rounded-full border transition-colors " +
                  (activeArea === g.areaKey
                    ? "bg-brand text-white border-brand"
                    : "bg-white text-gray-700 border-gray-200 hover:border-brand/40")
                }
                data-testid={`area-chip-${g.areaKey || "otros"}`}
              >
                {g.areaLabel}
                <span className="ml-1.5 text-[10px] opacity-70 font-mono-data">
                  {g.list.length}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* General offer ribbon — only for non-course-specific promotions.
            Per-course discounts already appear inside each course card. */}
        {activeArea === "all" && ribbonPromos.length > 0 && (
          <div className="mb-6 flex flex-wrap gap-2" data-testid="promo-ribbons">
            {ribbonPromos.map((promo) => (
              <span
                key={promo.id}
                className={`inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full text-white shadow-md transition-shadow ${
                  promo.kind === "quantity"
                    ? "bg-gradient-to-r from-violet-500 to-fuchsia-500 shadow-violet-500/30 hover:shadow-lg hover:shadow-violet-500/50"
                    : "bg-gradient-to-r from-[#5ab5aa] to-emerald-500 shadow-[#5ab5aa]/30 hover:shadow-lg hover:shadow-[#5ab5aa]/50"
                }`}
                data-testid={
                  promo.kind === "quantity"
                    ? `quantity-promo-badge-${promo.id}`
                    : "bulk-promo-badge"
                }
              >
                <span className="w-1.5 h-1.5 rounded-full bg-white/90 animate-pulse" aria-hidden="true" />
                <Tag className="w-3.5 h-3.5" />
                {promo.kind === "quantity"
                  ? `${promo.quantity_count} ${scopeNoun(promo)} por ${fmtPrice(promo.fixed_price || 0)}`
                  : `${promo.bulk_percent}% de descuento a partir de ${promo.bulk_min_courses} ${scopeNoun(promo)}`}
                {promo.ends_at && (
                  <CountdownBadge
                    endsAt={promo.ends_at}
                    onExpire={() => markPromoExpired(promo.id)}
                    className="bg-white/20 !text-white !border-white/30 !shadow-none"
                    testId={`${promo.kind}-promo-countdown-${promo.id}`}
                  />
                )}
              </span>
            ))}
          </div>
        )}

        {/* Package cards (virtual courses) — shown only on "Todas" */}
        {!loading && activeArea === "all" && packagePromos.length > 0 && (
          <section className="mb-10" data-testid="package-section">
            <div className="flex items-end justify-between gap-3 mb-4 border-b border-gray-200 pb-2">
              <h2 className="font-display text-2xl text-brand-ink flex items-center gap-2">
                <Package className="w-5 h-5 text-[#8a6f43]" />
                Paquetes cerrados
              </h2>
              <span className="text-xs text-gray-500 font-mono-data">
                {packagePromos.length} {packagePromos.length === 1 ? "pack" : "packs"}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {packagePromos.map((pkg) => (
                <Card
                  key={pkg.promo.id}
                  className={
                    "border shadow-none rounded-lg flex flex-col overflow-hidden transition-all " +
                    (pkg.fullySelected
                      ? "border-[#8a6f43] bg-[#fdf2d9]/40 ring-1 ring-[#e7d5b4]"
                      : "border-[#e7d5b4] bg-white hover:border-[#8a6f43]/60")
                  }
                  data-testid={`package-card-${pkg.promo.id}`}
                >
                  {/* Thumbnail strip of included courses */}
                  <div className="aspect-[16/9] w-full grid bg-[#fdf2d9]"
                       style={{ gridTemplateColumns: `repeat(${Math.min(pkg.items.length, 4)}, 1fr)` }}>
                    {pkg.items.slice(0, 4).map((c, idx) => {
                      const url = headerImageUrl(c);
                      return (
                        <div
                          key={c.id}
                          className={
                            "relative bg-gray-100 overflow-hidden border-[#e7d5b4] " +
                            (idx > 0 ? "border-l" : "")
                          }
                          title={c.title}
                          data-testid={`package-thumb-${pkg.promo.id}-${c.id}`}
                        >
                          {url ? (
                            <img src={url} alt={c.title} loading="lazy"
                                 className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center px-1 text-center text-[10px] text-[#8a6f43]">
                              {c.title}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {pkg.items.length > 4 && (
                      <div className="absolute bottom-2 right-2 text-[10px] font-bold bg-[#8a6f43] text-white px-1.5 py-0.5 rounded">
                        +{pkg.items.length - 4}
                      </div>
                    )}
                  </div>

                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <h3 className="font-display text-xl text-brand-ink leading-snug">
                        {pkg.promo.name}
                      </h3>
                      <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-full bg-[#fdf2d9] text-[#8a6f43] border border-[#e7d5b4] flex-shrink-0">
                        <Package className="w-2.5 h-2.5" /> Pack
                      </span>
                    </div>
                    <ul className="text-xs text-gray-600 mb-3 space-y-0.5 line-clamp-4">
                      {pkg.items.map((c) => (
                        <li key={c.id}>· {c.title}</li>
                      ))}
                    </ul>
                    <div className="flex items-end justify-between mt-auto pt-3 border-t border-[#e7d5b4]">
                      <div className="flex flex-col items-start">
                        {pkg.savings > 0 && (
                          <span
                            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full bg-gradient-to-r from-rose-500 to-red-500 text-white shadow-md shadow-rose-500/30 mb-2"
                            data-testid={`package-savings-${pkg.promo.id}`}
                          >
                            <Tag className="w-3 h-3" />
                            Ahorras {fmtPrice(pkg.savings)}
                          </span>
                        )}
                        {pkg.promo.ends_at && (
                          <CountdownBadge
                            endsAt={pkg.promo.ends_at}
                            onExpire={() => markPromoExpired(pkg.promo.id)}
                            className="mb-2"
                            testId={`package-countdown-${pkg.promo.id}`}
                          />
                        )}
                        <span className="text-[10px] text-gray-400 font-mono-data line-through">
                          {fmtPrice(pkg.originalSum)}
                        </span>
                        <span className="font-display text-2xl text-[#8a6f43]" data-testid={`package-price-${pkg.promo.id}`}>
                          {fmtPrice(pkg.promo.fixed_price || 0)}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant={pkg.fullySelected ? "default" : "outline"}
                        onClick={() => togglePackage(pkg)}
                        className={
                          pkg.fullySelected
                            ? "bg-[#8a6f43] hover:bg-[#6f5832] text-white"
                            : "border-[#8a6f43] text-[#8a6f43] hover:bg-[#fdf2d9]"
                        }
                        data-testid={`package-select-${pkg.promo.id}`}
                      >
                        {pkg.fullySelected ? "Quitar pack" : "Seleccionar pack"}
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </section>
        )}

        {loading ? (
          <p className="text-gray-500">Cargando catálogo…</p>
        ) : courses.length === 0 ? (
          <Card className="p-10 border border-gray-200 shadow-none rounded-lg text-center" data-testid="empty-catalog">
            <p className="text-gray-600">
              Aún no hay cursos disponibles para venta. Vuelve pronto.
            </p>
          </Card>
        ) : (
          <div className="space-y-10">
            {visibleGroups.map(({ areaKey, areaLabel, list }, groupIndex) => (
              <section key={areaKey || "__empty__"} data-testid={`area-section-${areaLabel}`}>
                <div className="flex items-end justify-between gap-3 mb-4 border-b border-gray-200 pb-2">
                  <h2 className="font-display text-2xl text-brand-ink">{areaLabel}</h2>
                  <span className="text-xs text-gray-500 font-mono-data">
                    {list.length} {list.length === 1 ? "curso" : "cursos"}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {/* Advertised codes take the first cells of the catalogue,
                      once, at the top — not repeated in every area. */}
                  {groupIndex === 0 &&
                    featuredCodes.map((offer) => (
                      <OfferCodeCard key={offer.id} offer={offer} />
                    ))}
                  {list.map((c) => {
              const isSelected = selected.has(c.id);
              const imgUrl = headerImageUrl(c);
              return (
                <Card
                  key={c.id}
                  className={`border shadow-none rounded-lg flex flex-col overflow-hidden transition-all ${
                    isSelected
                      ? "border-brand bg-brand/5 ring-1 ring-brand/30"
                      : "border-gray-200 bg-white hover:border-brand/40"
                  }`}
                  data-testid={`catalog-card-${c.id}`}
                >
                  {imgUrl && (
                    <div className="relative aspect-[16/9] w-full bg-gray-100 overflow-hidden" data-testid={`catalog-image-${c.id}`}>
                      <div
                        className={`absolute top-0 left-0 right-0 z-10 text-center text-[11px] font-bold tracking-[0.2em] uppercase py-1 text-white ${
                          c.kind === "final" ? "bg-brand-ink" : "bg-brand"
                        }`}
                        data-testid={`catalog-kind-band-${c.id}`}
                      >
                        {c.kind === "final" ? "CURSO" : "TALLER"}
                      </div>
                      <img
                        src={imgUrl}
                        alt={c.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="font-display text-xl text-brand-ink leading-snug">
                      {c.title}
                    </h3>
                    <span
                      className={
                        c.kind === "final"
                          ? "inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-full bg-[#fdf2d9] text-[#8a6f43] border border-[#e7d5b4] flex-shrink-0"
                          : "inline-flex items-center text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200 flex-shrink-0"
                      }
                    >
                      {c.kind === "final" ? "★ Curso · Diploma" : "Taller"}
                    </span>
                  </div>
                  {c.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-3">{c.description}</p>
                  )}
                  <div className="flex items-center gap-3 text-xs text-gray-500 mb-4">
                    {fmtHours(c.study_hours) && (
                      <span className="inline-flex items-center gap-1 font-mono-data">
                        <Clock className="w-3.5 h-3.5" /> {fmtHours(c.study_hours)}
                      </span>
                    )}
                    <span className="font-mono-data">{c.questions_count} preguntas</span>
                  </div>
                  <div className="flex items-end justify-between mt-auto pt-3 border-t border-gray-100">
                    <div className="flex flex-col items-start">
                      {(() => {
                        const coursePromo = activePromotions.find(
                          (p) => p.kind === "course" && p.course_id === c.id,
                        );
                        if (!coursePromo) return null;
                        return (
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span
                              className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full bg-gradient-to-r from-rose-500 to-red-500 text-white shadow-md shadow-rose-500/30"
                              data-testid={`course-promo-badge-${c.id}`}
                            >
                              <Tag className="w-3 h-3" />
                              {coursePromo.course_discount_type === "percent"
                                ? `−${coursePromo.course_discount_value}%`
                                : `−${fmtPrice(coursePromo.course_discount_value)}`}
                            </span>
                            {coursePromo.ends_at && (
                              <CountdownBadge
                                endsAt={coursePromo.ends_at}
                                onExpire={() => markPromoExpired(coursePromo.id)}
                                testId={`course-promo-countdown-${c.id}`}
                              />
                            )}
                          </div>
                        );
                      })()}
                      <span className="font-display text-2xl text-brand-ink" data-testid={`price-${c.id}`}>
                        {fmtPrice(c.price)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setInfoCourse(c)}
                        data-testid={`info-${c.id}`}
                      >
                        <Info className="w-3.5 h-3.5 mr-1" /> Más info
                      </Button>
                      <Button
                        size="sm"
                        variant={isSelected ? "default" : "outline"}
                        onClick={() => toggle(c.id)}
                        className={isSelected ? "bg-brand hover:bg-brand-hover text-white" : ""}
                        data-testid={`select-${c.id}`}
                      >
                        <Checkbox
                          checked={isSelected}
                          className="mr-2 pointer-events-none"
                          onCheckedChange={() => {}}
                        />
                        {isSelected ? "Seleccionado" : "Seleccionar"}
                      </Button>
                    </div>
                  </div>
                  </div>
                </Card>
              );
            })}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-gray-500">
            © {new Date().getFullYear()} laclaseencasa · SIMA Asistencial
          </p>
          <div className="flex items-center gap-4">
            <a
              href="https://www.sima.madrid/aviso-legal"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-medium text-brand hover:text-brand-hover transition-colors tracking-wide uppercase"
              data-testid="home-legal-notice-link"
            >
              Aviso legal
            </a>
            <a
              href="https://www.sima.madrid/politica-de-privacidad-y-cookies"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-medium text-brand hover:text-brand-hover transition-colors tracking-wide uppercase"
              data-testid="home-privacy-policy-link"
            >
              Política de privacidad y cookies
            </a>
          </div>
        </div>
      </footer>

      {/* Sticky cart footer */}
      {selected.size > 0 && (
        <div
          className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 shadow-[0_-4px_16px_-8px_rgba(0,0,0,0.1)] z-30"
          data-testid="cart-bar"
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-6 flex-wrap">
            <div className="flex items-center gap-6 text-sm flex-wrap">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-500">
                  Cursos
                </p>
                <p className="font-display text-2xl text-brand-ink" data-testid="cart-count">
                  {selected.size}
                </p>
              </div>
              <div className="hidden sm:block h-10 w-px bg-gray-200" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-500">Subtotal</p>
                <p className="font-mono-data text-base text-gray-700" data-testid="cart-subtotal">
                  {fmtPrice(cart.subtotal)}
                </p>
              </div>
              {cart.appliedPackages.map((p, i) => (
                <div key={`${p.promo.id}-${i}`} data-testid={`cart-pkg-${p.promo.id}`}>
                  <p className="text-[10px] uppercase tracking-wider text-[#8a6f43] inline-flex items-center gap-1">
                    <Package className="w-2.5 h-2.5" /> {p.promo.name}
                  </p>
                  <p className="font-mono-data text-base text-[#8a6f43]">
                    − {fmtPrice(p.savings)}
                  </p>
                </div>
              ))}
              {cart.lines.some((l) => l.discount > 0) && (
                <div data-testid="cart-course-promos-total">
                  <p className="text-[10px] uppercase tracking-wider text-rose-600 inline-flex items-center gap-1">
                    <Tag className="w-2.5 h-2.5" /> Descuentos curso
                  </p>
                  <p className="font-mono-data text-base text-rose-600">
                    − {fmtPrice(cart.lines.reduce((acc, l) => acc + l.discount, 0))}
                  </p>
                </div>
              )}
              {cart.bulkApplied && (
                <div data-testid="cart-bulk-promo">
                  <p className="text-[10px] uppercase tracking-wider text-brand">
                    {cart.bulkApplied.promo.name} ({cart.bulkApplied.percent}%)
                  </p>
                  <p className="font-mono-data text-base text-brand">
                    − {fmtPrice(cart.bulkApplied.amount)}
                  </p>
                </div>
              )}
              <div className="hidden sm:block h-10 w-px bg-gray-200" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-500">Total</p>
                <p className="font-display text-2xl text-brand-ink" data-testid="cart-total">
                  {fmtPrice(cart.total)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {cart.bulkApplied && (
                <span
                  className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-[#5ab5aa] text-white shadow-md shadow-emerald-500/30 animate-in fade-in zoom-in-95 duration-300"
                  data-testid="bulk-active-chip"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  ¡{Math.round(cart.bulkApplied.percent)}% activado!
                </span>
              )}
              {!cart.bulkApplied && bulkPromo && cart.lines.length > 0 && cart.lines.length < (bulkPromo.bulk_min_courses || 2) && (
                <span
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200"
                  data-testid="bulk-nudge-chip"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Añade {(bulkPromo.bulk_min_courses || 2) - cart.lines.length} curso{((bulkPromo.bulk_min_courses || 2) - cart.lines.length) === 1 ? "" : "s"} más y ahorra {Math.round(bulkPromo.bulk_percent)}%
                </span>
              )}
              {quantityNudge && (
                <span
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-violet-50 text-violet-800 border border-violet-200"
                  data-testid="quantity-nudge-chip"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Añade {quantityNudge.missing} {scopeNoun(quantityNudge.promo, quantityNudge.missing !== 1)} más
                  y llévate {quantityNudge.promo.quantity_count} por {fmtPrice(quantityNudge.promo.fixed_price || 0)}
                </span>
              )}
              <Button
                variant="ghost"
                onClick={() => setSelected(new Set())}
                data-testid="clear-cart"
              >
                Vaciar
              </Button>
              <Button
                onClick={() => setCheckoutOpen(true)}
                className="bg-brand hover:bg-brand-hover text-white"
                title="Pagar con tarjeta"
                data-testid="checkout-btn"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Pagar {fmtPrice(cart.total)}
              </Button>
            </div>
          </div>
          <p className="text-center text-[10px] text-gray-400 pb-2 -mt-2">
            Pago seguro con Stripe · Tarjeta, Apple Pay, Google Pay
          </p>
        </div>
      )}

      <CheckoutModal
        open={checkoutOpen}
        onOpenChange={setCheckoutOpen}
        courseIds={Array.from(selected)}
        total={cart.total}
      />

      {/* Info modal */}
      <Dialog open={!!infoCourse} onOpenChange={(o) => !o && setInfoCourse(null)}>
        {/* flex-col + gap-0 overrides the base DialogContent's `grid gap-4`:
            in a grid with a max-height, the auto-sized image row gets
            compressed once the text below overflows, so the content ended up
            overlapping the photo. In a flex column with `shrink-0` on the
            image, the photo always keeps its 16/9 box and the modal scrolls
            as a whole instead. */}
        <DialogContent className="max-w-lg p-0 flex flex-col gap-0" data-testid="info-modal">
          {infoCourse && (
            <>
              {headerImageUrl(infoCourse) && (
                <div className="aspect-[16/9] w-full shrink-0 bg-gray-100 overflow-hidden sm:rounded-t-lg" data-testid="info-modal-image">
                  <img
                    src={headerImageUrl(infoCourse)}
                    alt={infoCourse.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="p-6 space-y-4">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-brand-ink">
                  {infoCourse.title}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {infoCourse.more_info && infoCourse.more_info.trim() ? (
                  <div className="pr-1" data-testid="info-modal-content">
                    <MarkdownView text={infoCourse.more_info} />
                  </div>
                ) : infoCourse.description ? (
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {infoCourse.description}
                  </p>
                ) : (
                  <p className="text-sm text-gray-500 italic">Sin descripción.</p>
                )}
                <div className="grid grid-cols-3 gap-2 text-center pt-2 border-t border-gray-100">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-500">Tipo</p>
                    <p className="text-sm text-brand-ink font-medium">
                      {infoCourse.kind === "final" ? "Curso · Diploma" : "Taller · Certificado"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-500">Horas</p>
                    <p className="text-sm text-brand-ink font-medium">
                      {fmtHours(infoCourse.study_hours) || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-500">Precio</p>
                    <p className="text-sm text-brand font-bold font-mono-data">
                      {fmtPrice(infoCourse.price)}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setInfoCourse(null)}
                  >
                    Cerrar
                  </Button>
                  <Button
                    className="flex-1 bg-brand hover:bg-brand-hover text-white"
                    onClick={() => {
                      toggle(infoCourse.id);
                      setInfoCourse(null);
                    }}
                    data-testid="info-modal-select"
                  >
                    {selected.has(infoCourse.id) ? "Quitar selección" : "Añadir a la selección"}
                  </Button>
                </div>
              </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
